/**
 * Цвет «рамки» Telegram (шапка с кнопкой «Закрыть», фон под WebView, нижняя панель)
 * в тон текущей палитре приложения — иначе при тёмной палитре на светлом Telegram
 * сверху остаётся светлая системная полоса (и наоборот).
 *
 * Палитра задаётся атрибутом data-p на <html> (ThemePicker в основном приложении,
 * отдельный переключатель в админке) — следим за ним через MutationObserver,
 * поэтому синхронизация работает для любого места, где тему меняют.
 */

type TgChrome = {
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  isVersionAtLeast?: (version: string) => boolean;
};

function getTg(): TgChrome | null {
  try {
    return (window as unknown as { Telegram?: { WebApp?: TgChrome } }).Telegram?.WebApp ?? null;
  } catch {
    return null;
  }
}

function toHex(value: string): string | null {
  const v = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(v)) return v;
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(v);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`;
  const rgb = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})/i.exec(v);
  if (rgb) {
    return (
      '#' +
      [rgb[1], rgb[2], rgb[3]]
        .map((n) => Math.max(0, Math.min(255, parseInt(n, 10))).toString(16).padStart(2, '0'))
        .join('')
    );
  }
  return null;
}

export function syncTelegramChrome(): void {
  const tg = getTg();
  if (!tg || typeof tg.isVersionAtLeast !== 'function') return;
  let bg: string | null = null;
  try {
    bg = toHex(getComputedStyle(document.documentElement).getPropertyValue('--fc-bg'));
  } catch {
    bg = null;
  }
  if (!bg) return;
  const at = (v: string) => {
    try {
      return !!tg.isVersionAtLeast?.(v);
    } catch {
      return false;
    }
  };
  /** Произвольный #RRGGBB в шапке — с Bot API 6.9 (раньше только ключи темы Telegram). */
  if (at('6.9')) {
    try {
      tg.setHeaderColor?.(bg);
    } catch {
      /* ignore */
    }
  }
  if (at('6.1')) {
    try {
      tg.setBackgroundColor?.(bg);
    } catch {
      /* ignore */
    }
  }
  if (at('7.10')) {
    try {
      tg.setBottomBarColor?.(bg);
    } catch {
      /* ignore */
    }
  }
}

let started = false;

export function startTelegramChromeSync(): void {
  if (started || typeof document === 'undefined') return;
  started = true;
  syncTelegramChrome();
  try {
    const observer = new MutationObserver(() => syncTelegramChrome());
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-p'] });
  } catch {
    /* ignore */
  }
}
